import { QueryApiService } from 'src/common/api-service';
import { isNullOrWhiteSpaces } from 'src/shared/utils/strings';
import { getCachedJson, j2lStorageKeys, setCachedJson } from 'src/common/j2l-cache';
import { AuditableEntity } from '../common';

export interface Country extends AuditableEntity<number> {
  name: string;
  iso2: string;
  iso3: string;
  phoneCode: string;
}

export type CountryIso2 = { id: number; name: string; prefix: string; iso2: string };

interface CacheData {
  byId: Record<number, Country>;
  byIso2: Record<string, Country>;
}

const COUNTRIES_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

export class CountryApiService extends QueryApiService<Country> {
  private _cacheById: Record<number, Country> = {};
  private _cacheByIso2: Record<string, Country> = {};
  private _cacheKey = j2lStorageKeys.countriesTaxonomy();

  constructor() {
    super('countries');
  }

  public async loadCache(invalidate?: boolean) {
    console.log('loading countries cache');

    if (!invalidate) {
      const data = getCachedJson<CacheData>(this._cacheKey, COUNTRIES_CACHE_TTL_MS);
      if (data?.byId != null && data?.byIso2 != null) {
        this._cacheById = data.byId;
        this._cacheByIso2 = data.byIso2;
        return;
      }
    }

    const result = await this.getWithOData({ select: ['id', 'iso2', 'iso3', 'phoneCode', 'name'] });
    this._cacheById = (result?.value || []).reduce(
      (obj, item) => {
        obj[item.id] = item as Country;
        return obj;
      },
      {} as Record<string, Country>
    );
    this._cacheByIso2 = (result?.value || []).reduce(
      (obj, item) => {
        obj[item.iso2] = item as Country;
        return obj;
      },
      {} as Record<string, Country>
    );

    setCachedJson<CacheData>(this._cacheKey, {
      byId: this._cacheById,
      byIso2: this._cacheByIso2
    });
  }

  public getFromCacheById(id: number): Country {
    if (id == null) return null as any as Country;
    return this._cacheById[id];
  }
  public getFromCacheByIso2(iso2: string): Country {
    if (isNullOrWhiteSpaces(iso2)) return null as any as Country;
    return this._cacheByIso2[iso2];
  }
  public getAllFromCache(): Country[] {
    return Object.values(this._cacheById);
  }
}
